import { getProductUnitLabel as resolveProductUnitLabel } from "../config/productUnitOptions";

export interface TieredPriceRow {
  quantity: number;
  price: number;
}

export interface TieredPricingDisplayRow {
  volumeLabel: string;
  price: number;
}

/** USD price for product detail (e.g. $1,235.00). */
export function formatProductUsdPrice(amount: number): string {
  const n = Number(amount);
  if (!Number.isFinite(n)) return "$0.00";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);
}

/** Human-readable unit for cards and detail (e.g. "Piece/Pieces"). */
export function getProductUnitLabel(product: any): string {
  const raw = String(product?.unitOfMeasure || product?.moqUnit || "").trim();
  if (!raw) return "";
  return resolveProductUnitLabel(raw);
}

/** Card/detail line below price, e.g. "Per pieces". */
export function getProductPerUnitLine(product: any): string {
  const unit = getProductUnitLabel(product);
  return unit ? `Per ${unit}` : "";
}

/** Parse tiered pricing from API (array, JSON string, or object map). */
export function normalizeTieredPricing(raw: unknown): TieredPriceRow[] {
  if (raw == null || raw === "") return [];

  let data: unknown = raw;
  if (typeof raw === "string") {
    try {
      data = JSON.parse(raw);
      if (typeof data === "string") {
        try {
          data = JSON.parse(data);
        } catch {
          /* keep inner string */
        }
      }
    } catch {
      return [];
    }
  }

  let list: unknown[] = [];
  if (Array.isArray(data)) {
    list = data;
  } else if (data && typeof data === "object") {
    list = Object.values(data as Record<string, unknown>);
  } else {
    return [];
  }

  const rows: TieredPriceRow[] = [];
  for (const item of list) {
    if (!item || typeof item !== "object") continue;
    const o = item as Record<string, unknown>;
    const qRaw = o.quantity ?? o.unit ?? o.qty ?? o.minQuantity;
    const pRaw = o.price ?? o.unitPrice;
    if (
      qRaw === undefined ||
      qRaw === null ||
      pRaw === undefined ||
      pRaw === null
    ) {
      continue;
    }
    const quantity = Number(qRaw);
    const price = Number(pRaw);
    if (!Number.isFinite(quantity) || !Number.isFinite(price) || quantity <= 0) {
      continue;
    }
    rows.push({ quantity, price });
  }

  return rows.sort((a, b) => a.quantity - b.quantity);
}

export function applyProductDiscount(price: number, discountPct: number): number {
  const pct = Number(discountPct);
  if (!Number.isFinite(pct) || pct <= 0) return price;
  return price * (1 - pct / 100);
}

/** Split "Piece/Pieces" or similar into singular + plural forms. */
function parseUnitSingularPlural(unitLabel: string): {
  singular: string;
  plural: string;
} {
  const trimmed = unitLabel.trim();
  if (!trimmed) return { singular: "piece", plural: "pieces" };

  if (trimmed.includes("/")) {
    const [singularPart, pluralPart] = trimmed.split("/").map((s) => s.trim());
    const singular = singularPart || pluralPart || "piece";
    const plural = pluralPart || singularPart || "pieces";
    return { singular, plural };
  }

  const lower = trimmed.toLowerCase();
  if (lower.endsWith("ies") && lower.length > 3) {
    return { singular: lower.slice(0, -3) + "y", plural: lower };
  }
  if (lower.endsWith("es") && lower.length > 2) {
    const singular = lower.slice(0, -2);
    return { singular, plural: lower };
  }
  if (lower.endsWith("s") && lower.length > 1) {
    return { singular: lower.slice(0, -1), plural: lower };
  }
  return { singular: lower, plural: `${lower}s` };
}

/** e.g. 1 → "1 piece", 2 → "2 pieces" (uses product unit of measure). */
export function formatTierOrderQuantityLabel(
  quantity: number,
  unitLabel = "pieces",
): string {
  const qty = Math.max(1, Math.round(Number(quantity)));
  const { singular, plural } = parseUnitSingularPlural(unitLabel);
  const unit = qty === 1 ? singular : plural;
  return `${qty.toLocaleString("en-US")} ${unit}`;
}

/** One row per tier: exact min order qty (1 piece, 2 pieces, …) with unit price. */
export function getTieredPricingDisplayRows(
  tiers: TieredPriceRow[],
  unitLabel = "pieces",
): TieredPricingDisplayRow[] {
  const sorted = [...tiers].sort((a, b) => a.quantity - b.quantity);
  const unit = unitLabel.trim() || "pieces";

  return sorted.map((tier) => ({
    volumeLabel: formatTierOrderQuantityLabel(tier.quantity, unit),
    price: tier.price,
  }));
}

/** Headline range from tier prices, e.g. "$0.80 - $1.20". */
export function getTieredPriceRangeLabel(
  tiers: TieredPriceRow[],
  discountPct = 0,
): string | null {
  if (!tiers.length) return null;
  const prices = tiers.map((tier) =>
    applyProductDiscount(tier.price, discountPct),
  );
  const min = Math.min(...prices);
  const max = Math.max(...prices);
  if (min === max) return formatProductUsdPrice(min);
  return `${formatProductUsdPrice(min)} - ${formatProductUsdPrice(max)}`;
}

export function getProductPriceTerm(product: any): string {
  const terms: string[] = [];
  if (product?.fobPrice) terms.push("FOB");
  if (product?.cifPrice) terms.push("CIF");
  return terms.join(", ");
}

export function getProductPaymentMethod(product: any): string {
  if (Array.isArray(product?.paymentTerms)) {
    return product.paymentTerms.map((t: unknown) => String(t).trim()).filter(Boolean).join(", ");
  }
  if (product?.paymentTerms) return String(product.paymentTerms).trim();
  return "";
}

export function getProductPackingType(product: any): string {
  return String(product?.packaging || product?.packageDetails || "").trim();
}

/** B2B trade specs in display order for product detail pages. */
export function buildProductTradeSpecRows(product: any): { label: string; value: string }[] {
  if (!product) return [];
  const moq =
    product.moq != null && product.moq !== "" ? String(product.moq) : "";
  return [
    { label: "Minimum Order Qty", value: moq },
    { label: "Material Type", value: String(product.materialType || "").trim() },
    { label: "Size", value: String(product.size || "").trim() },
    { label: "Color", value: String(product.color || "").trim() },
    { label: "Price Term", value: getProductPriceTerm(product) },
    { label: "Payment method", value: getProductPaymentMethod(product) },
    { label: "Shipping Port", value: String(product.shippingPort || "").trim() },
    { label: "Packing Type", value: getProductPackingType(product) },
    { label: "Shelf Life", value: String(product.shelfLife || "").trim() },
    {
      label: "Other Specification",
      value: String(product.otherSpecification || "").trim(),
    },
    {
      label: "Brand Name",
      value: String(product.brand || product.brandName || "").trim(),
    },
    {
      label: "Shipping Time",
      value: String(product.deliveryTime || product.shippingTime || "").trim(),
    },
  ];
}
